<template>
  <div :class="['bg-black flex flex-col', rootHeightClass]">
    <FlashMessage />

    <!-- Navbar superior -->
    <header
      v-if="!isRegistroRoute"
      :class="[
        'relative items-center justify-center px-0 sm:px-3 sm:py-1',
        'shadow-md animate-fade-in overflow-hidden reflective-bar',
        'max-h-24 opacity-100 py-0.5',
        'sm:max-h-none sm:opacity-100 sm:py-1!',
        hideHeaderOnMobile ? 'hidden sm:flex' : 'flex'
      ]"
    >
      <div class="reflective-glint" aria-hidden="true"></div>

      <!-- Controles de sesión: esquina superior derecha en móvil (donde hay espacio libre), izquierda en escritorio -->
      <div class="absolute right-0 top-0 bottom-2 z-20 flex flex-col items-end justify-between gap-2 sm:inset-y-auto sm:top-2 sm:right-auto sm:bottom-auto sm:left-2 sm:flex-row sm:items-center sm:justify-start">
        <div v-if="hasSession || !isHomeRoute" class="animate-fade-in flex items-center gap-0.5 rounded-full border border-white/15 bg-black/30 p-1 shadow-lg shadow-black/20 backdrop-blur-md sm:gap-1 sm:p-1.5">
          <!-- Botón candado -->
          <button
            v-if="hasSession"
            @click="handleLogout"
            class="flex h-6 items-center justify-center gap-0.5 rounded-full px-1.5 transition duration-300 hover:bg-white/10 active:scale-95 sm:h-10 sm:gap-1.5 sm:px-3"
            title="Cerrar sesión"
            aria-label="Cerrar sesión"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 shrink-0 text-yellow-400 sm:h-6 sm:w-6">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
          <button
            v-else
            @click="handleLogin"
            class="flex h-6 items-center justify-center gap-0.5 rounded-full px-1.5 transition duration-300 hover:bg-white/10 active:scale-95 sm:h-10 sm:gap-1.5 sm:px-3"
            title="Iniciar sesión"
            aria-label="Iniciar sesión"
          ></button>

          <div class="h-5 w-px bg-white/15"></div>

          <!-- Botón casa -->
          <button
            @click="handleHome"
            class="flex h-6 items-center justify-center gap-0.5 rounded-full px-1.5 transition duration-300 hover:bg-white/10 active:scale-95 sm:h-10 sm:gap-1.5 sm:px-3"
            title="Ir al inicio"
            aria-label="Ir al inicio"
          >
            <HomeIcon class="h-4 w-4 shrink-0 text-yellow-400 sm:h-6 sm:w-6" />
          </button>
        </div>

        <!-- Saludo -->
        <div
          v-if="userName"
          class="animate-fade-in flex items-center gap-1 rounded-full border border-white/15 bg-black/30 px-2 py-0.5 shadow-lg shadow-black/20 backdrop-blur-md sm:gap-1.5 sm:px-3 sm:py-1.5"
        >
          <span class="h-1 w-1 shrink-0 rounded-full bg-emerald-400 sm:h-1.5 sm:w-1.5"></span>
          <span class="text-[10px] font-semibold text-white drop-shadow sm:text-sm md:text-base">Hola, {{ userName }}</span>
        </div>
      </div>

      <!-- Logo: corona + "Agencia Nevadas". El archivo logo11.webp trae
           margen transparente propio (4.6% izq., 2.7% der.) que ningún
           margin/padding externo puede recortar. En móvil se usa transform
           (no width/margin, para no interferir con el justify-content:center
           del header) para agrandar y desplazar el arte a la izquierda; el
           sobrante lo recorta el overflow-hidden del header. -->
      <img
        src="@/assets/logo11.webp"
        alt="Agencia Nevadas"
        :class="[
          'mt-1.5 sm:mt-0 sm:mx-auto w-full max-w-none sm:max-w-110 md:max-w-145',
          'origin-left scale-[1.04] translate-x-[-2.6%] sm:scale-100 sm:translate-x-0',
          'h-auto object-contain select-none pointer-events-none',
          (hasSession || !isHomeRoute) ? 'pr-20 sm:pr-0' : ''
        ]"
      />
    </header>

    <!-- Contenido principal -->
    <main :class="['flex-1 min-h-0 bg-transparent flex flex-col', mainPaddingClass]">
      <router-view />
    </main>

    <!-- Footer -->
    <footer
      v-if="!isRegistroRoute && !homeCityLandingVisible"
      :class="['text-white reflective-footer', (isClientePerfilRoute || hideFooterOnMobile) ? 'hidden sm:block' : '', isHomeRoute ? 'footer-home-mobile-light' : '']"
    >
      <div class="reflective-glint" aria-hidden="true"></div>

      <div class="grid grid-cols-3 items-center gap-2 sm:gap-3 px-4 md:px-8 py-0 sm:py-3">
        <!-- Botón Publicar (izquierda). Para el administrador, este botón se
             muestra centrado (ver más abajo) en lugar de a la izquierda. -->
        <div class="flex justify-start">
          <button
            v-if="showPublicarButtonLeft"
            @click="handlePublicar"
            :class="[
              hidePublicarButtonOnMobile ? 'hidden sm:inline-block' : '',
              'relative whitespace-nowrap text-sm sm:text-base md:text-lg lg:text-2xl font-semibold text-white',
              'transition duration-300 hover:opacity-70',
              'max-sm:before:absolute max-sm:before:-inset-x-4 max-sm:before:inset-y-0 max-sm:before:content-[\'\']'
            ]"
          >
            Publicar
          </button>

          <button
            v-if="(hasSession && isHomeRoute) || isRegistroClienteRoute"
            type="button"
            @click="handleLogout"
            class="relative flex h-9 w-9 items-center justify-center text-white transition duration-300 hover:opacity-70 sm:hidden
                   max-sm:before:absolute max-sm:before:-inset-x-4 max-sm:before:inset-y-0 max-sm:before:content-['']"
            title="Cerrar sesión"
            aria-label="Cerrar sesión"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>

        <!-- Centro: para el administrador, el botón Agregar (sin lupa ni mapa);
             para el resto, la lupa de búsqueda por ciudad. Con sesión (Publicar
             centrado) o con la lupa de invitado, va en la columna del medio de
             verdad (order-1); solo otros casos (ninguno actualmente) usarían
             la última columna en mobile (order-3). -->
        <div
          :class="[
            'flex sm:order-0 sm:justify-center',
            showPublicarButtonCentered || showBuscarButton ? 'order-1 justify-center' : 'order-3 justify-end'
          ]"
        >
          <button
            v-if="showPublicarButtonCentered"
            @click="handlePublicar"
            class="relative whitespace-nowrap text-lg sm:text-xl md:text-2xl font-semibold text-white
                   transition duration-300 hover:opacity-70
                   max-sm:before:absolute max-sm:before:-inset-x-4 max-sm:before:inset-y-0 max-sm:before:content-['']"
          >
            {{ isAdminSession ? 'Agregar' : 'Publicar' }}
          </button>

          <!-- En mobile, para visitante sin sesión en la home, esta lupa se
               movió arriba de "Nuestro Catálogo" (ver HomePage.vue); acá solo
               queda visible en desktop (sm:flex), donde sigue abriendo el
               abanico de ciudades de siempre. -->
          <div v-if="showBuscarButton" class="relative hidden min-h-9 sm:flex sm:min-h-11 md:min-h-14 items-center">
            <div
              v-if="citySearchOpen"
              class="fixed inset-0 z-40"
              @click="closeCitySearch"
            ></div>

            <button
              ref="lupaButtonRef"
              v-if="!citySearchOpen"
              type="button"
              @click="toggleCitySearch"
              class="relative z-50 flex h-9 w-9 translate-x-0 items-center justify-center rounded-full bg-[#FACC15] text-black
                     sm:h-11 sm:w-11 sm:translate-x-0 sm:rounded-none sm:bg-transparent sm:text-white
                     transition duration-300 hover:opacity-70 md:h-14 md:w-14
                     max-sm:before:absolute max-sm:before:-inset-x-4 max-sm:before:inset-y-0 max-sm:before:content-['']"
              title="Buscar por ciudad"
              aria-label="Buscar por ciudad"
            >
              <MagnifyingGlassIcon class="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7" />
            </button>

            <div
              v-if="citySearchOpen"
              class="city-search-panel fixed z-50 flex flex-wrap items-center justify-center gap-2"
              :style="citySearchPanelStyle"
            >
              <button
                v-for="(option, idx) in citySearchOptions"
                :key="option.value"
                type="button"
                :title="option.title || option.label"
                class="city-search-option flex h-10 min-w-10 items-center justify-center whitespace-nowrap rounded-full bg-black border border-white/15 px-2.5 text-[11px] sm:h-11 sm:min-w-11 sm:px-3 sm:text-xs font-semibold text-white shadow-lg transition duration-150 hover:bg-neutral-800 active:bg-neutral-700 active:shadow-sm"
                :style="{ animationDelay: `${idx * 35}ms` }"
                @click.stop="handleCitySearchOptionClick(option.value)"
              >
                {{ option.label }}<span class="ml-1 font-normal text-slate-300">({{ option.count }})</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Derecha: Mapa escorts (usuarios) o, para el administrador,
             engranaje de gestión de tipos de servicio en el mismo lugar. -->
        <div class="order-2 flex justify-end sm:order-0 sm:justify-end">
          <!-- Oculto en mobile a pedido (no eliminar): la lupa de búsqueda
               por ciudad ahora va centrada en el medio del footer (ver el
               bloque de búsqueda, más arriba), no en este espacio. -->
          <button
            v-if="showMapaButton"
            @click="handleMapa"
            class="relative hidden items-center gap-0.5 whitespace-nowrap text-sm font-semibold text-white
                   transition duration-300 hover:opacity-70 sm:flex sm:gap-1 sm:text-base md:text-lg lg:text-2xl
                   max-sm:before:absolute max-sm:before:-inset-x-4 max-sm:before:inset-y-0 max-sm:before:content-['']"
          >
            <MapPinIcon class="h-5 w-4 sm:h-8 sm:w-5 lg:h-11 lg:w-6 shrink-0 text-red-600" />
            Mapa escort
          </button>

          <button
            v-if="isAdminSession"
            type="button"
            @click="openServiciosModal"
            class="relative flex h-9 w-9 items-center justify-center text-white transition duration-300 hover:opacity-70 sm:h-14 sm:w-14
                   max-sm:before:absolute max-sm:before:-inset-x-4 max-sm:before:inset-y-0 max-sm:before:content-['']"
            title="Gestionar tipos de servicio"
            aria-label="Gestionar tipos de servicio"
          >
            <Cog6ToothIcon class="h-4 w-4 text-white sm:h-7 sm:w-7" />
          </button>
        </div>
      </div>

    </footer>

    <!-- Modal admin: gestionar tipos de servicio -->
    <div
      v-if="showServiciosModal"
      class="fixed inset-0 z-70 flex items-center justify-center bg-white"
      @click.self="closeServiciosModal"
    >
      <div class="flex h-full w-full max-w-none flex-col overflow-hidden bg-white">
        <div class="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-6 py-4">
          <h3 class="text-xl font-black text-slate-800">Administración</h3>
          <button
            type="button"
            @click="closeServiciosModal"
            class="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-200 hover:text-plomo"
            aria-label="Cerrar"
          >
            <XMarkIcon class="h-5 w-5" />
          </button>
        </div>

        <div class="flex shrink-0 gap-1 border-b border-slate-200 bg-slate-50 px-4 pt-2">
          <button
            type="button"
            @click="serviciosModalTab = 'servicios'"
            class="rounded-t-xl px-4 py-2 text-sm font-semibold transition"
            :class="serviciosModalTab === 'servicios' ? 'bg-white text-slate-800 border border-b-0 border-slate-200' : 'text-slate-500 hover:text-plomo'"
          >
            Servicios
          </button>
          <button
            type="button"
            @click="serviciosModalTab = 'precios'"
            class="rounded-t-xl px-4 py-2 text-sm font-semibold transition"
            :class="serviciosModalTab === 'precios' ? 'bg-white text-slate-800 border border-b-0 border-slate-200' : 'text-slate-500 hover:text-plomo'"
          >
            Precios y QR
          </button>
        </div>

        <div v-if="serviciosModalTab === 'servicios'" class="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <div class="flex gap-2">
            <input
              v-model="nuevoServicioNombre"
              type="text"
              placeholder="Nuevo tipo de servicio"
              maxlength="60"
              class="w-full rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-800 focus:border-carmesi focus:ring-2 focus:ring-carmesi/20"
              @keyup.enter="addServicioType"
            />
            <button
              type="button"
              :disabled="!nuevoServicioNombre.trim() || isSavingServicio"
              @click="addServicioType"
              class="shrink-0 rounded-2xl border border-amber-300/30 bg-linear-to-r from-amber-400 via-yellow-300 to-amber-400 px-4 py-2.5 text-sm font-bold text-slate-950 shadow-md transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {{ isSavingServicio ? 'Guardando...' : 'Agregar' }}
            </button>
          </div>

          <div class="mt-4 space-y-2">
            <p v-if="serviciosAdminLoading" class="py-4 text-center text-sm text-slate-400">
              Cargando tipos de servicio...
            </p>
            <p v-else-if="serviciosAdmin.length === 0" class="py-4 text-center text-sm text-slate-400">
              Todavía no hay tipos de servicio registrados.
            </p>

            <div
              v-for="servicio in serviciosAdminOrdenados"
              :key="servicio.id_servicio"
              class="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5"
            >
              <span class="truncate text-sm font-medium text-slate-800 lowercase">{{ servicio.descripcion }}</span>

              <div v-if="confirmingDeleteServicioId === servicio.id_servicio" class="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  class="rounded-xl border border-slate-400 bg-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-300"
                  @click="cancelDeleteServicioType"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  :disabled="deletingServicioId === servicio.id_servicio"
                  class="rounded-xl border border-red-400/30 bg-red-600 px-2.5 py-1 text-xs font-bold text-white transition hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                  @click="deleteServicioType(servicio)"
                >
                  {{ deletingServicioId === servicio.id_servicio ? '...' : 'Eliminar' }}
                </button>
              </div>
              <button
                v-else
                type="button"
                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                title="Eliminar tipo de servicio"
                aria-label="Eliminar tipo de servicio"
                @click="askDeleteServicioType(servicio.id_servicio)"
              >
                <TrashIcon class="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <div v-else class="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <!-- Alta de un nuevo precio con su propio QR -->
          <div class="space-y-2 rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <div class="flex gap-2">
              <input
                v-model="nuevoPrecioMonto"
                type="number"
                min="0"
                placeholder="Precio (Bs)"
                class="w-full rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-800 focus:border-carmesi focus:ring-2 focus:ring-carmesi/20"
              />
              <input
                v-model="nuevoPrecioDias"
                type="number"
                min="1"
                placeholder="Días"
                class="w-full rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-800 focus:border-carmesi focus:ring-2 focus:ring-carmesi/20"
              />
            </div>

            <div class="flex items-center gap-3">
              <button
                type="button"
                @click="nuevoPrecioQrInputRef?.click()"
                class="flex items-center gap-1.5 rounded-2xl border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-800 transition hover:bg-slate-100"
              >
                <ArrowUpTrayIcon class="h-4 w-4" />
                {{ nuevoPrecioQrFile ? 'Cambiar QR' : 'Elegir QR' }}
              </button>
              <img
                v-if="nuevoPrecioQrPreviewUrl"
                :src="nuevoPrecioQrPreviewUrl"
                alt="Vista previa QR"
                class="h-12 w-12 shrink-0 rounded-lg border border-slate-200 object-cover"
              />
              <input
                ref="nuevoPrecioQrInputRef"
                type="file"
                accept="image/*"
                class="hidden"
                @change="onNuevoPrecioQrSelected"
              />
            </div>

            <button
              type="button"
              :disabled="!nuevoPrecioMonto || !nuevoPrecioDias || isSavingPrecio"
              @click="addPrecioType"
              class="w-full rounded-2xl border border-amber-300/30 bg-linear-to-r from-amber-400 via-yellow-300 to-amber-400 px-4 py-2.5 text-sm font-bold text-slate-950 shadow-md transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {{ isSavingPrecio ? 'Guardando...' : 'Agregar precio' }}
            </button>
          </div>

          <div class="mt-4 space-y-2">
            <p v-if="preciosAdminLoading" class="py-4 text-center text-sm text-slate-400">
              Cargando precios...
            </p>
            <p v-else-if="preciosAdmin.length === 0" class="py-4 text-center text-sm text-slate-400">
              Todavía no hay precios registrados.
            </p>

            <div
              v-for="precioItem in preciosAdmin"
              :key="precioItem.id_precio_publicacion"
              class="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5"
            >
              <img
                v-if="resolveImageUrl(precioItem.qr_url)"
                :src="resolveImageUrl(precioItem.qr_url)"
                alt="QR de pago"
                class="h-12 w-12 shrink-0 rounded-lg border border-slate-200 object-cover"
              />
              <div v-else class="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-dashed border-slate-300 text-slate-300">
                <QrCodeIcon class="h-6 w-6" />
              </div>

              <div class="min-w-0 flex-1">
                <p class="text-xs text-slate-800">{{ precioItem.precio }} Bs · {{ precioItem.numero_dias }} días</p>
              </div>

              <button
                type="button"
                :disabled="uploadingQrId === precioItem.id_precio_publicacion"
                @click="triggerReplaceQr(precioItem.id_precio_publicacion)"
                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-200 hover:text-plomo disabled:cursor-not-allowed disabled:opacity-50"
                title="Cargar / reemplazar QR"
                aria-label="Cargar o reemplazar QR"
              >
                <ArrowUpTrayIcon v-if="uploadingQrId !== precioItem.id_precio_publicacion" class="h-4 w-4" />
                <span v-else class="text-[10px]">...</span>
              </button>

              <div v-if="confirmingDeletePrecioId === precioItem.id_precio_publicacion" class="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  class="rounded-xl border border-slate-400 bg-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-300"
                  @click="cancelDeletePrecioType"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  :disabled="deletingPrecioId === precioItem.id_precio_publicacion"
                  class="rounded-xl border border-red-400/30 bg-red-600 px-2.5 py-1 text-xs font-bold text-white transition hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                  @click="deletePrecioType(precioItem)"
                >
                  {{ deletingPrecioId === precioItem.id_precio_publicacion ? '...' : 'Eliminar' }}
                </button>
              </div>
              <button
                v-else
                type="button"
                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                title="Eliminar precio"
                aria-label="Eliminar precio"
                @click="askDeletePrecioType(precioItem.id_precio_publicacion)"
              >
                <TrashIcon class="h-4 w-4" />
              </button>
            </div>
          </div>

          <input
            ref="replaceQrInputRef"
            type="file"
            accept="image/*"
            class="hidden"
            @change="onReplaceQrSelected"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>

import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import router from './router'
import FlashMessage from './components/FlashMessage.vue'
import api, { setAuthToken } from './services/api'
import { showMessage } from './services/flash'
import {
  HomeIcon,
  MagnifyingGlassIcon,
  Cog6ToothIcon,
  TrashIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
  QrCodeIcon
} from '@heroicons/vue/24/outline'
import { MapPinIcon } from '@heroicons/vue/24/solid'
import { homeCityLandingVisible, homeMapOpen } from './homeCityLandingState'


const showPublicarButton = computed(() => {
  const name = router.currentRoute?.value?.name
  return name !== 'registro-cliente' && name !== 'registro' && name !== 'cliente-perfil'
})

const isHomeRoute = computed(() => router.currentRoute?.value?.name === 'home')
const isRegistroRoute = computed(() => router.currentRoute?.value?.name === 'registro')
const isRegistroClienteRoute = computed(() => router.currentRoute?.value?.name === 'registro-cliente')
const isClientePerfilRoute = computed(() => router.currentRoute?.value?.name === 'cliente-perfil')
const hideHeaderOnMobile = computed(() => isHomeRoute.value || isRegistroClienteRoute.value || isClientePerfilRoute.value)

// homeCityLandingVisible (ver ./homeCityLandingState.js) es un ref
// compartido que HomePage.vue escribe directamente cuando muestra/oculta su
// pantalla de bienvenida de "buscar por ciudad" en el primer cargado móvil:
// esa pantalla ya trae su propio botón de publicar, así que mientras está
// abierta ocultamos el footer del sitio para no duplicarlo.

// Salvavidas: esta bandera solo tiene sentido mientras estamos parados en
// "home" (la pantalla de bienvenida vive adentro de HomePage.vue). Si por
// algún cruce de eventos (p. ej. el flujo ClientePerfil -> "ver en mapa" ->
// cerrar mapa -> volver al perfil -> atrás del navegador, que remonta
// HomePage.vue varias veces con historial manipulado a mano) quedara en
// `true` sin su evento `visible:false` correspondiente, el footer se perdía
// para siempre en cualquier home futuro. Apenas se navega fuera de home la
// forzamos a `false`: si el próximo HomePage sí necesita mostrar su pantalla
// de bienvenida, va a volver a pedirlo con su propio evento.
watch(isHomeRoute, (isHome) => {
  if (!isHome) {
    homeCityLandingVisible.value = false
    homeMapOpen.value = false
  }
})

// Con sesión iniciada (cliente registrado o administrador) no se usa la lupa
// ni el mapa de escorts: en su lugar, el botón Publicar se muestra centrado,
// donde normalmente va la lupa.
const showPublicarButtonCentered = computed(() => isHomeRoute.value && hasSession.value && showPublicarButton.value)
const showPublicarButtonLeft = computed(() => showPublicarButton.value && !showPublicarButtonCentered.value)

// El botón "Mapa escorts" y la lupa solo tienen sentido en la página de inicio,
// y no se muestran con sesión iniciada.
const showMapaButton = computed(() => isHomeRoute.value && !hasSession.value)
const showBuscarButton = computed(() => isHomeRoute.value && !hasSession.value && !homeMapOpen.value)

// Visitante (sin sesión) en el home: en mobile se oculta "Publicar" y la
// lupa pasa a ocupar el centro del footer (antes quedaba a la derecha,
// rellenando el hueco que dejaba "Mapa escort", oculto en mobile).
const hidePublicarButtonOnMobile = computed(() => isHomeRoute.value && !hasSession.value)

// Footer oculto del todo en mobile para visitante sin sesión en el home: ya
// no tenía nada visible ahí (Publicar y la lupa/mapa se ocultan en mobile
// para este caso, ver arriba), y quitarlo permite que el documento
// scrollee de verdad (ver rootHeightClass) para habilitar el "pull to
// refresh" nativo del navegador en el feed de HomePage.vue.
const hideFooterOnMobile = computed(() => isHomeRoute.value && !hasSession.value)

// homeMapOpen (ver ./homeCityLandingState.js): ref compartido que
// HomePage.vue escribe directamente cuando el mapa de escorts (overlay de
// pantalla completa dentro de HomePage.vue) está abierto, para que
// ocultemos la lupa mientras tanto.

// Búsqueda por ciudad: se despliega en abanico alrededor del botón de la lupa.
// Los 9 departamentos de Bolivia se muestran siempre, tenga o no clientes
// registrados en cada uno (si no hay, el filtro simplemente no arroja resultados).
const BOLIVIA_DEPARTMENTS = ['La Paz', 'Cochabamba', 'Santa Cruz', 'Oruro', 'Potosí', 'Chuquisaca', 'Tarija', 'Beni', 'Pando']
  .sort((a, b) => a.localeCompare(b, 'es'))

const citySearchOpen = ref(false)
const lupaButtonRef = ref(null)
const lupaAnchor = ref({ x: 0, y: 0 })

// Cantidad de publicaciones por ciudad, informada por HomePage.vue (que es
// quien tiene la data de clientes) vía CustomEvent, para mostrarla en cada
// botón del abanico.
const cityPublicationCounts = ref({})
function handleCityCountsUpdate(event) {
  cityPublicationCounts.value = event.detail?.counts || {}
}
onMounted(() => window.addEventListener('city-counts-update', handleCityCountsUpdate))
onUnmounted(() => window.removeEventListener('city-counts-update', handleCityCountsUpdate))

const citySearchOptions = computed(() => {
  if (!citySearchOpen.value) return []

  return BOLIVIA_DEPARTMENTS
    .filter((name) => (cityPublicationCounts.value[name] || 0) > 0)
    .map((name) => ({
      value: name,
      label: name,
      count: cityPublicationCounts.value[name] || 0
    }))
})

// Las ciudades quedan visibles solo 5 segundos: si el usuario no elige
// ninguna a tiempo, el abanico se cierra solo.
const CITY_SEARCH_AUTO_CLOSE_MS = 5000
let citySearchAutoCloseTimer = null

function clearCitySearchAutoCloseTimer() {
  if (citySearchAutoCloseTimer) {
    clearTimeout(citySearchAutoCloseTimer)
    citySearchAutoCloseTimer = null
  }
}

function startCitySearchAutoCloseTimer() {
  clearCitySearchAutoCloseTimer()
  citySearchAutoCloseTimer = setTimeout(() => {
    closeCitySearch()
  }, CITY_SEARCH_AUTO_CLOSE_MS)
}

function toggleCitySearch() {
  if (citySearchOpen.value) {
    closeCitySearch()
    return
  }
  // En móvil, la lupa abre la pantalla completa de bienvenida de HomePage.vue
  // (ESCORTS + ciudades) en vez del abanico de botones de acá abajo.
  if (typeof window !== 'undefined' && window.innerWidth < 768) {
    window.dispatchEvent(new CustomEvent('open-home-city-landing'))
    return
  }
  const rect = lupaButtonRef.value?.getBoundingClientRect?.()
  if (rect) {
    lupaAnchor.value = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
  }
  citySearchOpen.value = true
  startCitySearchAutoCloseTimer()
}

function closeCitySearch() {
  clearCitySearchAutoCloseTimer()
  citySearchOpen.value = false
}

function handleCitySearchOptionClick(city) {
  window.dispatchEvent(new CustomEvent('apply-city-type-filter', {
    detail: { city, type: '' }
  }))
  closeCitySearch()
}

onUnmounted(() => clearCitySearchAutoCloseTimer())

// El panel de opciones (ciudades o tipo) es un contenedor flex con gap fijo,
// así el espacio entre botones queda siempre simétrico sin importar el ancho
// de cada etiqueta. Se ancla por su borde inferior justo arriba de la lupa
// (position:fixed, para no quedar recortado por el overflow:hidden del
// footer); si el contenido no entra en una fila, el propio flex-wrap agrega
// filas hacia arriba.
const CITY_SEARCH_PANEL_GAP = 14

// La lupa vive pegada al borde derecho del footer en mobile (ver
// footer-home-mobile-light): centrar el panel sobre ese punto (comportamiento
// normal, ver .city-search-panel) lo saca de pantalla por la derecha. Si el
// ancla queda muy cerca del borde, lo alineamos por su borde derecho en vez
// de centrarlo.
const CITY_SEARCH_EDGE_THRESHOLD = 100

const citySearchPanelStyle = computed(() => {
  const { x, y } = lupaAnchor.value
  const top = `${y - CITY_SEARCH_PANEL_GAP}px`
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : x * 2

  if (viewportWidth - x < CITY_SEARCH_EDGE_THRESHOLD) {
    return { left: 'auto', right: '12px', top, transform: 'translateY(-100%)' }
  }

  return { left: `${x}px`, top }
})

const mainPaddingClass = computed(() => {
  const name = router.currentRoute?.value?.name
  if (name === 'registro') return 'pt-0 pb-0'
  return name === 'registro-cliente' ? 'pt-0 pb-0 sm:pt-10' : 'pt-0 pb-0 sm:pt-4 sm:pb-2'
})

// El perfil de cliente debe caber siempre en una sola pantalla (sin scroll),
// así que fijamos la altura exacta del layout en esa ruta. En "home", en
// vista móvil también fijamos la altura del layout (header y footer quedan
// fijos) para que el scroll ocurra solo dentro del listado de publicaciones,
// nunca en la página completa; desde md hacia arriba vuelve a min-h-screen.
const rootHeightClass = computed(() => {
  const name = router.currentRoute?.value?.name
  if (name === 'cliente-perfil') return 'h-dvh overflow-hidden'
  if (name === 'home') {
    // El shell fijo (scroll solo del listado, footer siempre visible) sigue
    // haciendo falta con sesión (admin/cliente logueado), que sí usa el
    // footer en mobile. Para visitante sin sesión el footer queda oculto
    // (ver hideFooterOnMobile) y dejamos que el documento scrollee de
    // verdad, así el navegador puede disparar su propio pull-to-refresh.
    if (!hasSession.value) return 'min-h-screen'
    return 'h-dvh overflow-hidden md:h-auto md:overflow-visible md:min-h-screen'
  }
  return 'min-h-screen'
})

// Referencia inocua para evitar que algunas configuraciones de linter/webpack
// marquen la variable como "asignada pero no usada" cuando no detectan su uso
// desde la plantilla SFC. Silenciamos reglas específicas que dan error.
// eslint-disable-next-line no-unused-vars, vue/no-ref-as-operand
void mainPaddingClass

// Los computeds de sesión leen localStorage directo, así que no tienen
// ninguna dependencia reactiva propia: por defecto quedarían cacheados en
// su primer valor para siempre. Antes se forzaba un window.location.href
// (recarga completa) tras el login para que recalcularan — pero eso además
// destruye el historial de navegación en memoria de esta SPA (rompiendo el
// "piso" de vuelta atrás de más abajo). En su lugar, sessionVersion se
// incrementa on el evento 'auth-session-changed' (ver loginUser en
// RegistroPage.vue) y cada computed lo referencia para saber que debe
// recalcularse, sin necesidad de recargar la página.
const sessionVersion = ref(0)
function handleAuthSessionChanged() {
  sessionVersion.value++
}
onMounted(() => window.addEventListener('auth-session-changed', handleAuthSessionChanged))
onUnmounted(() => window.removeEventListener('auth-session-changed', handleAuthSessionChanged))

const userName = computed(() => {
  void sessionVersion.value
  const user = localStorage.getItem('user')
  if (!user) return ''
  try {
    const parsed = JSON.parse(user)
    return parsed?.nombre || parsed?.usuario || parsed?.name || ''
  } catch {
    return ''
  }
})

const hasSession = computed(() => {
  void sessionVersion.value
  const token = localStorage.getItem('token')
  return !!token && token !== 'null' && token !== 'undefined'
})

// "Piso" de navegación: mientras haya sesión activa (admin o cliente), no
// se puede volver con el botón/gesto "atrás" más atrás de la primera vez
// que se llegó al home ya logueado (sea porque se acaba de iniciar sesión,
// o porque se abrió la app y ya había una sesión guardada). Antes, dar
// "atrás" suficientes veces podía terminar mostrando el home tal como
// estaba antes de loguearse; ahora el navegador nos avisa (afterEach) cada
// vez que la posición del historial cae por debajo de ese piso y volvemos
// para adelante hasta él.
let floorHistoryPosition = null
function currentHistoryPosition() {
  const state = window.history.state
  return state && typeof state.position === 'number' ? state.position : null
}
// Leemos localStorage directo (no vía el computed hasSession): un computed
// sin dependencias reactivas queda cacheado en su primer valor para
// siempre (nada le avisa que localStorage cambió), así que para esta
// comprobación —que necesita el dato realmente actualizado en cada
// navegación— lo recalculamos a mano en cada afterEach.
function isSessionActiveNow() {
  const token = localStorage.getItem('token')
  return !!token && token !== 'null' && token !== 'undefined'
}
router.afterEach(() => {
  const active = isSessionActiveNow()
  const pos = currentHistoryPosition()

  if (!active) {
    floorHistoryPosition = null
    return
  }

  if (floorHistoryPosition === null) {
    // Primera navegación con sesión activa detectada (recién logueado, o
    // la app se abrió con una sesión ya guardada): ese es el piso.
    floorHistoryPosition = pos
    return
  }

  if (pos !== null && pos < floorHistoryPosition) {
    router.go(floorHistoryPosition - pos)
  }
})

function extractRoleId(source) {
  if (!source || typeof source !== 'object') return null
  const candidates = [source.id_rol, source.idRol, source.rol_id, source.rolId, source.role_id, source.roleId]
  for (const value of candidates) {
    const parsed = Number.parseInt(value, 10)
    if (Number.isFinite(parsed)) return parsed
  }
  return null
}

const isAdminSession = computed(() => {
  void sessionVersion.value
  const rawUser = localStorage.getItem('user') || sessionStorage.getItem('user') || localStorage.getItem('authUser') || sessionStorage.getItem('authUser')
  if (rawUser) {
    try {
      if (extractRoleId(JSON.parse(rawUser)) === 1) return true
    } catch {
      // ignoramos si el usuario guardado no es JSON válido
    }
  }

  const token = localStorage.getItem('token') || sessionStorage.getItem('token')
  if (token && token.split('.').length === 3) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
      return extractRoleId(payload) === 1
    } catch {
      return false
    }
  }

  return false
})

function handlePublicar() {
  const token = localStorage.getItem('token')
  const hasToken = !!token && token !== 'null' && token !== 'undefined'

  if (hasToken) {
    router.push('/registro-cliente')
  } else {
    router.push('/registro')
  }
}

function handleMapa() {
  if (router.currentRoute?.value?.name === 'home') {
    window.dispatchEvent(new CustomEvent('open-escorts-map'))
  } else {
    router.push('/').then(() => {
      setTimeout(() => window.dispatchEvent(new CustomEvent('open-escorts-map')), 400)
    })
  }
}

function handleLogout() {
  localStorage.removeItem('token')
  localStorage.removeItem('user')
  setAuthToken(null)
  window.location.href = '/'
}

function handleLogin() {
  router.push('/registro')
}

function handleHome() {
  router.push('/')
}

// Gestión de tipos de servicio (solo administrador): agregar o eliminar
// entradas de nevadas.servicio, usadas luego como opciones al registrar clientes.
const showServiciosModal = ref(false)
const serviciosModalTab = ref('servicios')
const serviciosAdmin = ref([])
const serviciosAdminLoading = ref(false)
const nuevoServicioNombre = ref('')
const isSavingServicio = ref(false)
const confirmingDeleteServicioId = ref(null)
const deletingServicioId = ref(null)

const serviciosAdminOrdenados = computed(() =>
  [...serviciosAdmin.value].sort((a, b) =>
    (a.descripcion || '').localeCompare(b.descripcion || '', 'es', { sensitivity: 'base' })
  )
)

function resolveImageUrl(url) {
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url

  const baseUrl = api.defaults?.baseURL || ''
  if (!baseUrl) return url

  return `${baseUrl.replace(/\/$/, '')}/${String(url).replace(/^\//, '')}`
}

async function loadServiciosAdmin() {
  serviciosAdminLoading.value = true
  try {
    const response = await api.get('/servicios')
    serviciosAdmin.value = Array.isArray(response?.data) ? response.data : []
  } catch (error) {
    console.error('Error cargando tipos de servicio:', error)
    showMessage('error', 'No se pudieron cargar los tipos de servicio.')
    serviciosAdmin.value = []
  } finally {
    serviciosAdminLoading.value = false
  }
}

function openServiciosModal() {
  showServiciosModal.value = true
  serviciosModalTab.value = 'servicios'
  confirmingDeleteServicioId.value = null
  loadServiciosAdmin()
  loadPreciosAdmin()
  // Empujamos una entrada de historial marcada (?adminModal=1) para que el
  // botón/gesto "atrás" del celular cierre este modal en vez de salir de la
  // página; el watch de más abajo es quien realmente oculta el modal cuando
  // esa marca desaparece de la URL (ver closeServiciosModal).
  router.push({ query: { ...router.currentRoute.value.query, adminModal: '1' } }).catch(() => {})
}

function closeServiciosModal() {
  // Si el modal se abrió empujando esa entrada al historial, cerramos yendo
  // "atrás" para consumirla -así el botón atrás del celular y el botón X
  // quedan sincronizados, sin dejar una entrada extra en el historial-. El
  // watch de abajo es el que realmente oculta el modal y limpia el
  // formulario, una vez que la navegación de vuelta ya ocurrió (mismo
  // camino tanto si cierra por atrás como por la X).
  if (router.currentRoute.value.query.adminModal) {
    router.back()
  } else {
    showServiciosModal.value = false
  }
}

watch(
  () => router.currentRoute.value.query.adminModal,
  (adminModal) => {
    if (adminModal || !showServiciosModal.value) return
    showServiciosModal.value = false
    nuevoServicioNombre.value = ''
    confirmingDeleteServicioId.value = null
    resetNuevoPrecioForm()
    confirmingDeletePrecioId.value = null
  }
)

async function addServicioType() {
  const descripcion = nuevoServicioNombre.value.trim()
  if (!descripcion || isSavingServicio.value) return

  isSavingServicio.value = true
  try {
    const response = await api.post('/servicios', { descripcion, estado: 'activo' })
    if (response?.data) serviciosAdmin.value = [...serviciosAdmin.value, response.data]
    nuevoServicioNombre.value = ''
    showMessage('success', `Tipo de servicio "${descripcion}" agregado.`)
  } catch (error) {
    console.error('Error creando tipo de servicio:', error)
    showMessage('error', error?.response?.data?.message || 'No se pudo agregar el tipo de servicio.')
  } finally {
    isSavingServicio.value = false
  }
}

function askDeleteServicioType(id) {
  confirmingDeleteServicioId.value = id
}

function cancelDeleteServicioType() {
  confirmingDeleteServicioId.value = null
}

async function deleteServicioType(servicio) {
  const id = servicio?.id_servicio
  if (!id || deletingServicioId.value) return

  deletingServicioId.value = id
  try {
    await api.delete(`/servicios/${id}`)
    serviciosAdmin.value = serviciosAdmin.value.filter((s) => s.id_servicio !== id)
    showMessage('success', `Tipo de servicio "${servicio.descripcion}" eliminado.`)
  } catch (error) {
    console.error('Error eliminando tipo de servicio:', error)
    showMessage('error', error?.response?.data?.message || 'No se pudo eliminar el tipo de servicio.')
  } finally {
    deletingServicioId.value = null
    confirmingDeleteServicioId.value = null
  }
}

// Gestión de precios de publicación y su QR de pago (solo administrador):
// permite cargar/reemplazar el QR de un precio existente o crear un precio
// nuevo con su propio QR, usadas luego al elegir el plan de pago al publicar.
const preciosAdmin = ref([])
const preciosAdminLoading = ref(false)
const nuevoPrecioMonto = ref('')
const nuevoPrecioDias = ref('')
const nuevoPrecioQrFile = ref(null)
const nuevoPrecioQrPreviewUrl = ref('')
const nuevoPrecioQrInputRef = ref(null)
const isSavingPrecio = ref(false)
const confirmingDeletePrecioId = ref(null)
const deletingPrecioId = ref(null)
const uploadingQrId = ref(null)
const replaceQrInputRef = ref(null)
const replaceQrTargetId = ref(null)

async function loadPreciosAdmin() {
  preciosAdminLoading.value = true
  try {
    const response = await api.get('/precio-publicacion')
    preciosAdmin.value = Array.isArray(response?.data) ? response.data : []
  } catch (error) {
    console.error('Error cargando precios de publicación:', error)
    showMessage('error', 'No se pudieron cargar los precios.')
    preciosAdmin.value = []
  } finally {
    preciosAdminLoading.value = false
  }
}

function resetNuevoPrecioForm() {
  nuevoPrecioMonto.value = ''
  nuevoPrecioDias.value = ''
  nuevoPrecioQrFile.value = null
  if (nuevoPrecioQrPreviewUrl.value) URL.revokeObjectURL(nuevoPrecioQrPreviewUrl.value)
  nuevoPrecioQrPreviewUrl.value = ''
  if (nuevoPrecioQrInputRef.value) nuevoPrecioQrInputRef.value.value = ''
}

function onNuevoPrecioQrSelected(event) {
  const file = event?.target?.files?.[0] || null
  if (nuevoPrecioQrPreviewUrl.value) URL.revokeObjectURL(nuevoPrecioQrPreviewUrl.value)
  nuevoPrecioQrFile.value = file
  nuevoPrecioQrPreviewUrl.value = file ? URL.createObjectURL(file) : ''
}

async function addPrecioType() {
  const precio = Number(nuevoPrecioMonto.value)
  const numeroDias = Number(nuevoPrecioDias.value)
  if (!precio || !numeroDias || isSavingPrecio.value) return

  // El backend exige una descripción; se genera sola a partir del precio,
  // ya no se le pide un nombre de plan al administrador.
  const descripcion = `Plan Bs ${precio}`

  isSavingPrecio.value = true
  try {
    const formData = new FormData()
    formData.append('descripcion', descripcion)
    formData.append('precio', precio)
    formData.append('numero_dias', numeroDias)
    formData.append('estado', 'ACTIVO')
    if (nuevoPrecioQrFile.value) formData.append('qr', nuevoPrecioQrFile.value)

    const response = await api.post('/precio-publicacion', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    if (response?.data) preciosAdmin.value = [...preciosAdmin.value, response.data]
    resetNuevoPrecioForm()
    showMessage('success', `Precio "${descripcion}" agregado.`)
  } catch (error) {
    console.error('Error creando precio de publicación:', error)
    showMessage('error', error?.response?.data?.message || 'No se pudo agregar el precio.')
  } finally {
    isSavingPrecio.value = false
  }
}

function triggerReplaceQr(id) {
  if (uploadingQrId.value) return
  replaceQrTargetId.value = id
  replaceQrInputRef.value?.click()
}

async function onReplaceQrSelected(event) {
  const file = event?.target?.files?.[0] || null
  const id = replaceQrTargetId.value
  if (!file || !id) return

  uploadingQrId.value = id
  try {
    const formData = new FormData()
    formData.append('qr', file)

    const response = await api.put(`/precio-publicacion/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    if (response?.data) {
      preciosAdmin.value = preciosAdmin.value.map((item) =>
        item.id_precio_publicacion === id ? response.data : item
      )
    }
    showMessage('success', 'QR actualizado correctamente.')
  } catch (error) {
    console.error('Error actualizando el QR del precio:', error)
    showMessage('error', error?.response?.data?.message || 'No se pudo actualizar el QR.')
  } finally {
    uploadingQrId.value = null
    replaceQrTargetId.value = null
    if (replaceQrInputRef.value) replaceQrInputRef.value.value = ''
  }
}

function askDeletePrecioType(id) {
  confirmingDeletePrecioId.value = id
}

function cancelDeletePrecioType() {
  confirmingDeletePrecioId.value = null
}

async function deletePrecioType(precioItem) {
  const id = precioItem?.id_precio_publicacion
  if (!id || deletingPrecioId.value) return

  deletingPrecioId.value = id
  try {
    await api.delete(`/precio-publicacion/${id}`)
    preciosAdmin.value = preciosAdmin.value.filter((p) => p.id_precio_publicacion !== id)
    showMessage('success', `Precio "${precioItem.descripcion}" eliminado.`)
  } catch (error) {
    console.error('Error eliminando precio de publicación:', error)
    showMessage('error', error?.response?.data?.message || 'No se pudo eliminar el precio.')
  } finally {
    deletingPrecioId.value = null
    confirmingDeletePrecioId.value = null
  }
}

onUnmounted(() => {
  if (nuevoPrecioQrPreviewUrl.value) URL.revokeObjectURL(nuevoPrecioQrPreviewUrl.value)
})
</script>

<style>
:root {
  --bone-white: #ffffff; /* ahora blanco puro */
}

/* Misma tipografía en toda la app: información de tarjetas, botones, títulos, etc. */
html, body, #app, button, input, select, textarea {
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

/* Sin esto, el navegador espera ~300ms tras el primer toque por si viene un
   segundo toque (gesto de doble-tap-para-hacer-zoom) antes de disparar el
   click; en un dispositivo real, con la carga de imágenes/animaciones del
   home compitiendo por el hilo principal justo en ese instante, ese primer
   toque se siente "perdido" y hay que tocar dos veces (ej.: botón Publicar).
   touch-action: manipulation le dice al navegador que este elemento no hace
   zoom con doble-tap, así el click se dispara de inmediato en el primer
   toque. */
button, a, [role="button"] {
  touch-action: manipulation;
}

/* Aplicar color de texto blanco hueso globalmente */
html, body, #app {
  color: var(--bone-white);
}

/* Efecto de encenderse y apagarse para los botones del footer (Publicar / Mapa escorts) */
.blink-footer-btn {
  animation: footerBtnBlink 1.6s ease-in-out infinite;
}

@keyframes footerBtnBlink {
  0%, 100% {
    opacity: 1;
    box-shadow: 0 0 10px 2px rgba(48, 50, 54, 0.45);
  }
  50% {
    opacity: 0.55;
    box-shadow: 0 0 0 0 rgba(48, 50, 54, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .blink-footer-btn {
    animation: none;
  }
}

/* Panel de opciones de ciudad/tipo que se despliega arriba de la lupa.
   position:fixed (definido en el template) evita que el overflow:hidden del
   footer lo recorte. Se ancla por su borde inferior: si flex-wrap agrega
   filas, el panel crece hacia arriba solo. */
.city-search-panel {
  transform: translate(-50%, -100%);
  max-width: calc(100vw - 24px);
}

.city-search-option {
  animation: citySearchOptionPop 0.32s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

/* Efecto de "apretar botón" al elegir ciudad/tipo. Se marca !important porque
   el fill-mode:both de citySearchOptionPop deja fijado el transform del
   keyframe, que de otro modo pisaría el transform de :active. */
.city-search-option:active {
  transform: scale(0.88) !important;
}

@keyframes citySearchOptionPop {
  from {
    opacity: 0;
    transform: scale(0.4);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .city-search-option {
    animation: none;
  }
}

.min-h-screen {
  color: var(--bone-white) !important;
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fade-in 1.5s ease-out forwards;
}

/* En móvil se quita el efecto de aparición del header (y con él, del logo) al cargar. */
@media (max-width: 639px) {
  header.reflective-bar.animate-fade-in {
    animation: none;
    opacity: 1;
    transform: none;
  }
}

/* Estilos personalizados para efecto reflectante en header y footer */
.reflective-bar,
.reflective-footer {
  /* Graphite Gray base with subtle gradient and illuminated effect */
  --graphite-base: #4B4E53;
  --graphite-dark: #34363a;
  --graphite-light: #6b6e73;
    background: linear-gradient(90deg, var(--graphite-dark) 0%, var(--graphite-base) 50%, var(--graphite-light) 100%) !important; 
  box-shadow: 0 12px 26px rgba(0,0,0,0.22), inset 0 -8px 20px rgba(0,0,0,0.18);
  border-radius: 0 !important;
  position: relative;
  overflow: hidden; /* clip decorative elements to avoid visible bottom strip */
  -webkit-background-clip: padding-box;
  background-clip: padding-box;
  border: none; /* remove any visible border that would show white corners */
  color: #f8fafc; /* texto claro para contraste */
}

/* Remove rounded corners specifically for header and footer to avoid white edges */
header.reflective-bar {
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
  background: #000000 !important;
  /* Sombra bien contenida para que no deje una franja de color debajo del header */
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25), inset 0 -8px 20px rgba(0, 0, 0, 0.18);
}

/* Reduce el reflejo brillante superior del header */
header.reflective-bar::before {
  background: linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0.02));
}

footer.reflective-footer {
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  border-radius: 0 !important;
  /* Footer del mismo color que el contenido del homepage */
  background: #000000 !important;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.4);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  color: #ffffff;
}

/* Footer del home en mobile: mismo negro que el resto del footer (antes era
   blanco "a pedido"; se revirtió a pedido también, para que combine con el
   fondo negro del contenido de la home). */
@media (max-width: 639px) {
  footer.reflective-footer.footer-home-mobile-light {
    background: #000000 !important;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    color: #ffffff;
  }
}

.reflective-bar::before,
.reflective-footer::before {
  /* top glossy highlight */
  content: "";
  position: absolute;
  left: 6%;
  right: 6%;
  top: 4%;
  height: 34%;
  background: linear-gradient(180deg, rgba(255,255,255,0.42), rgba(255,255,255,0.06));
  filter: blur(5px);
  transform: skewY(-1.0deg);
  border-radius: 0 !important;
  pointer-events: none;
  mix-blend-mode: overlay;
}

.reflective-footer::after {
  /* subtle outer glow */
  content: "";
  position: absolute;
    left: 0;
    right: 0;
    bottom: -6px;
      height: 8px;
      background: linear-gradient(180deg, rgba(75,78,83,0.08) 0%, rgba(75,78,83,0) 80%);
      filter: blur(4px);
  pointer-events: none;
}

/* El header no debe dejar ningún resplandor debajo: ahí empieza el contenido */
header.reflective-bar::after {
  display: none;
}

/* animated glint sweep */
.reflective-bar .glint,
.reflective-footer .glint {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.reflective-glint {
  position: absolute;
  left: -40%;
  top: -10%;
  width: 60%;
  height: 140%;
  background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%);
  transform: skewX(-20deg);
  filter: blur(8px);
  animation: glint-sweep 3.6s linear infinite;
  mix-blend-mode: screen;
}

@keyframes glint-sweep {
  0% { transform: translateX(-120%) skewX(-20deg); opacity: 0 }
  10% { opacity: 0.6 }
  50% { transform: translateX(120%) skewX(-20deg); opacity: 0.18 }
  100% { transform: translateX(240%) skewX(-20deg); opacity: 0 }
}

/* Ajustes responsivos */
@media (max-width: 768px) {
  .reflective-bar::before {
    left: 6%;
    right: 6%;
    height: 28%;
    top: 8%;
  }
}
</style>
